import { CSSObject, SerializedStyles, css } from '@emotion/react'
import React, { ComponentPropsWithRef, forwardRef, useEffect, useState } from 'react'
import 'react-datepicker/dist/react-datepicker.css'
import dayjs, { Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { ErrorMessage } from '@/components/atoms/error-message'
import { labelStyle } from '@/styles/form'
import { RequiredMark } from '@/components/atoms/required-mark'
import { CUSTOM_FORM } from '@/components/template/custom-design-style'
import { DateCalendar } from '@/components/atoms/date-calendar'
import { getSpaceAvailableTimeByMonth, getStartAvailabilityTimes } from '@/features/space/spaces-slice'
import { globalHotelId } from '@/hooks/use-hotel-id'
import { useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/app/hooks'

interface Props {
  maxDate?: Dayjs | Date
  name: string
  value?: string
  buttonCss?: SerializedStyles | CSSObject
  errorMessage?: string
  label?: string
  required?: boolean
  quantity: number
  onSetValue?: (date: string) => void
}

export const SpaceReserveDateCalendar: React.FC<ComponentPropsWithRef<'button'> & Props> = forwardRef(
  ({ maxDate, name, value, buttonCss, errorMessage, label, required, quantity, onSetValue }, ref) => {
    const { assetId, spaceId } = useParams<{ assetId: string; spaceId: string }>()
    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    const { spaceDetailsByDay } = useAppSelector(state => state.spaces)

    const [isOpen, setIsOpen] = useState(false)
    const [selectDate, setSelectDate] = useState<any>(value)
    const [outOfStockDays, setOutOfStockDays] = useState<Date[]>([])
    const [loadedMonths, setLoadedMonths] = useState<string[]>([])

    const onSettlement = (date: Date) => {
      setSelectDate(date)
      setIsOpen(false)
    }

    const onOpenCalendar = () => {
      fetchViewCalendarMonthStocks()
      setIsOpen(true)
    }

    const fetchViewCalendarMonthStocks = (date?: Date) => {
      const viewCalendarDate = date ? dayjs(date) : dayjs(selectDate)
      const loadMonth = viewCalendarDate.format('YYYY-MM')
      if (loadedMonths.includes(loadMonth)) {
        return
      }
      setLoadedMonths([...loadedMonths, loadMonth])

      dispatch(
        getSpaceAvailableTimeByMonth({
          hotelId: globalHotelId(),
          guestAppAssetId: assetId as string,
          spaceId: spaceId as string,
          useDay: dayjs(viewCalendarDate),
        }),
      )
    }

    useEffect(() => {
      const _outOfStockDays: Date[] = []
      Object.keys(spaceDetailsByDay).forEach(dayKey => {
        if (getStartAvailabilityTimes(spaceDetailsByDay[dayKey], quantity).length === 0) {
          _outOfStockDays.push(dayjs(dayKey).toDate())
        }
      })
      setOutOfStockDays([...outOfStockDays, ..._outOfStockDays])
    }, [spaceDetailsByDay])

    useEffect(() => {
      setLoadedMonths([])
      setOutOfStockDays([])
    }, [spaceId])

    return (
      <>
        {isOpen && (
          <DateCalendar
            maxDate={maxDate}
            date={selectDate}
            onSettlement={onSettlement}
            onClose={() => setIsOpen(false)}
            onMonthChange={fetchViewCalendarMonthStocks}
            onSetValue={onSetValue}
            excludeDates={outOfStockDays}
          />
        )}
        {label && (
          <label css={labelStyle}>
            {label} {required && <RequiredMark />}
          </label>
        )}
        <div css={ButtonWrapStyle} className="select-wrapper">
          <button
            ref={ref}
            type="button"
            name={name}
            css={[datePickerButtonStyle, buttonCss]}
            onClick={onOpenCalendar}
            id="checkInDate"
            className={CUSTOM_FORM}
          >
            {selectDate ? dayjs(selectDate).format('YYYY/MM/DD') : t('Please select')}
          </button>
        </div>
        {errorMessage && <ErrorMessage text={errorMessage} />}
      </>
    )
  },
)
SpaceReserveDateCalendar.displayName = 'SpaceReserveDateCalendar'

const ButtonWrapStyle = css({
  width: '100%',
  position: 'relative',
})

const datePickerButtonStyle = css({
  width: '100%',
  height: '44px',
  appearance: 'none',
  textAlign: 'left',
  paddingLeft: '20px',
  '::placeholder': {
    color: '#CCCCCC',
  },
})
